import React from 'react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const HOW_HEARD_OPTIONS = [
  { value: 'google', label: 'Google Search' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Friend/Family Referral' },
  { value: 'veteran-org', label: 'Veteran Organization' },
  { value: 'job-board', label: 'Job Board' },
  { value: 'military-transition', label: 'Military Transition Program' },
  { value: 'employer-referral', label: 'Employer Referral' },
  { value: 'networking-event', label: 'Networking Event' },
  { value: 'other', label: 'Other' },
];

export const CandidateInfoSection = ({ formData, validationErrors, onInputChange, onSelectChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Candidate Information</h3>

    <div>
      <Label htmlFor="fullLegalName">Full Legal Name *</Label>
      <Input
        id="fullLegalName"
        name="fullLegalName"
        required
        value={formData.fullLegalName}
        onChange={onInputChange}
        className={`mt-1 ${validationErrors.fullLegalName ? 'border-red-500' : ''}`}
        data-testid="input-full-legal-name"
      />
      {validationErrors.fullLegalName && (
        <p className="text-red-500 text-sm mt-1" data-testid="error-full-legal-name">{validationErrors.fullLegalName}</p>
      )}
    </div>

    <div>
      <Label htmlFor="preferredName">Preferred Name</Label>
      <Input
        id="preferredName"
        name="preferredName"
        value={formData.preferredName}
        onChange={onInputChange}
        className="mt-1"
        data-testid="input-preferred-name"
      />
    </div>

    <div>
      <Label htmlFor="cityState">City / State *</Label>
      <Input
        id="cityState"
        name="cityState"
        required
        placeholder="e.g., Cincinnati, OH"
        value={formData.cityState}
        onChange={onInputChange}
        className={`mt-1 ${validationErrors.cityState ? 'border-red-500' : ''}`}
        data-testid="input-city-state"
      />
      {validationErrors.cityState && (
        <p className="text-red-500 text-sm mt-1" data-testid="error-city-state">{validationErrors.cityState}</p>
      )}
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="phoneNumber">Phone Number *</Label>
        <Input
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          required
          value={formData.phoneNumber}
          onChange={onInputChange}
          className={`mt-1 ${validationErrors.phoneNumber ? 'border-red-500' : ''}`}
          data-testid="input-phone-number"
        />
        {validationErrors.phoneNumber && (
          <p className="text-red-500 text-sm mt-1" data-testid="error-phone-number">{validationErrors.phoneNumber}</p>
        )}
      </div>
      <div>
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          required
          value={formData.email}
          onChange={onInputChange}
          className={`mt-1 ${validationErrors.email ? 'border-red-500' : ''}`}
          data-testid="input-email"
        />
        {validationErrors.email && (
          <p className="text-red-500 text-sm mt-1" data-testid="error-email">{validationErrors.email}</p>
        )}
      </div>
    </div>

    <div>
      <Label htmlFor="howDidYouHear">How did you hear about Service2Success?</Label>
      <Select
        value={formData.howDidYouHear || undefined}
        onValueChange={(value) => onSelectChange('howDidYouHear', value)}
      >
        <SelectTrigger className="mt-1" data-testid="select-how-did-you-hear">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {HOW_HEARD_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label htmlFor="currentEmploymentStatus">Current Employment Status *</Label>
      <Select
        value={formData.currentEmploymentStatus || undefined}
        onValueChange={(value) => onSelectChange('currentEmploymentStatus', value)}
      >
        <SelectTrigger className={`mt-1 ${validationErrors.currentEmploymentStatus ? 'border-red-500' : ''}`} data-testid="select-employment-status">
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="employed">Currently Employed</SelectItem>
          <SelectItem value="unemployed">Unemployed / Seeking</SelectItem>
          <SelectItem value="transitioning">Military Transitioning</SelectItem>
          <SelectItem value="student">Student</SelectItem>
        </SelectContent>
      </Select>
      {validationErrors.currentEmploymentStatus && (
        <p className="text-red-500 text-sm mt-1" data-testid="error-employment-status">{validationErrors.currentEmploymentStatus}</p>
      )}
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="currentEmployer">Current / Most Recent Employer</Label>
        <Input
          id="currentEmployer"
          name="currentEmployer"
          value={formData.currentEmployer}
          onChange={onInputChange}
          className="mt-1"
          data-testid="input-current-employer"
        />
      </div>
      <div>
        <Label htmlFor="currentJobTitle">Current Job Title</Label>
        <Input
          id="currentJobTitle"
          name="currentJobTitle"
          value={formData.currentJobTitle}
          onChange={onInputChange}
          className="mt-1"
          data-testid="input-job-title"
        />
      </div>
    </div>

    <div>
      <Label htmlFor="datesOfEmployment">Dates of Employment</Label>
      <Input
        id="datesOfEmployment"
        name="datesOfEmployment"
        placeholder="e.g., Jan 2020 - Present"
        value={formData.datesOfEmployment}
        onChange={onInputChange}
        className="mt-1"
        data-testid="input-dates-employment"
      />
    </div>
  </div>
);

export const ProfessionalBackgroundSection = ({ formData, onInputChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Professional Background</h3>

    <div>
      <Label htmlFor="primaryResponsibilities">Primary responsibilities in your current or most recent role</Label>
      <Textarea id="primaryResponsibilities" name="primaryResponsibilities" rows={4} value={formData.primaryResponsibilities} onChange={onInputChange} className="mt-1" />
    </div>
    <div>
      <Label htmlFor="employeesSupervised">Number of employees / technicians supervised (if applicable)</Label>
      <Input id="employeesSupervised" name="employeesSupervised" placeholder="e.g., 5-10 direct reports" value={formData.employeesSupervised} onChange={onInputChange} className="mt-1" />
    </div>
    <div>
      <Label htmlFor="equipmentTechnologies">Types of equipment, systems, or technologies you work with</Label>
      <Textarea id="equipmentTechnologies" name="equipmentTechnologies" rows={3} value={formData.equipmentTechnologies} onChange={onInputChange} className="mt-1" />
    </div>
    <div>
      <Label htmlFor="keyAccomplishments">Key accomplishments, awards, or recognitions</Label>
      <Textarea id="keyAccomplishments" name="keyAccomplishments" rows={3} value={formData.keyAccomplishments} onChange={onInputChange} className="mt-1" />
    </div>
  </div>
);

export const MilitaryBackgroundSection = ({ formData, onInputChange, onSelectChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Military Background (if applicable)</h3>
    <p className="text-sm text-gray-600 mb-4">Skip this section if not applicable</p>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="branchOfService">Branch of Service</Label>
        <Select value={formData.branchOfService || undefined} onValueChange={(value) => onSelectChange('branchOfService', value)}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="army">Army</SelectItem>
            <SelectItem value="navy">Navy</SelectItem>
            <SelectItem value="air-force">Air Force</SelectItem>
            <SelectItem value="marines">Marines</SelectItem>
            <SelectItem value="coast-guard">Coast Guard</SelectItem>
            <SelectItem value="space-force">Space Force</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="yearsOfService">Years of Service</Label>
        <Input id="yearsOfService" name="yearsOfService" placeholder="e.g., 4 years" value={formData.yearsOfService} onChange={onInputChange} className="mt-1" />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="rankAtSeparation">Rank at Separation</Label>
        <Input id="rankAtSeparation" name="rankAtSeparation" placeholder="e.g., E-5, O-3" value={formData.rankAtSeparation} onChange={onInputChange} className="mt-1" />
      </div>
      <div>
        <Label htmlFor="militaryOccupation">MOS / Military Occupation</Label>
        <Input id="militaryOccupation" name="militaryOccupation" placeholder="e.g., 11B Infantry" value={formData.militaryOccupation} onChange={onInputChange} className="mt-1" />
      </div>
    </div>

    <div>
      <Label htmlFor="militaryDuties">Key duties or equipment maintained</Label>
      <Textarea id="militaryDuties" name="militaryDuties" rows={3} value={formData.militaryDuties} onChange={onInputChange} className="mt-1" />
    </div>
  </div>
);

export const EducationSection = ({ formData, onInputChange, onSelectChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Education & Certifications</h3>

    <div>
      <Label htmlFor="highestEducation">Highest level of education completed</Label>
      <Select value={formData.highestEducation || undefined} onValueChange={(value) => onSelectChange('highestEducation', value)}>
        <SelectTrigger className="mt-1"><SelectValue placeholder="Select education level" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="high-school">High School / GED</SelectItem>
          <SelectItem value="some-college">Some College</SelectItem>
          <SelectItem value="associates">Associate's Degree</SelectItem>
          <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
          <SelectItem value="masters">Master's Degree</SelectItem>
          <SelectItem value="doctorate">Doctorate</SelectItem>
          <SelectItem value="trade">Trade School / Technical Training</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="collegeUniversity">College / University</Label>
        <Input id="collegeUniversity" name="collegeUniversity" value={formData.collegeUniversity} onChange={onInputChange} className="mt-1" />
      </div>
      <div>
        <Label htmlFor="degreeMajor">Degree / Major</Label>
        <Input id="degreeMajor" name="degreeMajor" value={formData.degreeMajor} onChange={onInputChange} className="mt-1" />
      </div>
    </div>

    <div>
      <Label htmlFor="tradeSchool">Trade school / technical training</Label>
      <Input id="tradeSchool" name="tradeSchool" value={formData.tradeSchool} onChange={onInputChange} className="mt-1" />
    </div>

    <div>
      <Label htmlFor="certifications">Professional certifications</Label>
      <Textarea id="certifications" name="certifications" rows={3} placeholder="List any relevant certifications, licenses, or credentials" value={formData.certifications} onChange={onInputChange} className="mt-1" />
    </div>
  </div>
);

export const JobSearchSection = ({ formData, validationErrors, onInputChange, onSelectChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Job Search Preferences</h3>

    <div>
      <Label htmlFor="desiredJobTitles">Desired job titles / roles *</Label>
      <Input
        id="desiredJobTitles" name="desiredJobTitles" required
        placeholder="e.g., Operations Manager, Maintenance Supervisor"
        value={formData.desiredJobTitles} onChange={onInputChange}
        className={`mt-1 ${validationErrors.desiredJobTitles ? 'border-red-500' : ''}`}
        data-testid="input-desired-job-titles"
      />
      {validationErrors.desiredJobTitles && (
        <p className="text-red-500 text-sm mt-1" data-testid="error-desired-job-titles">{validationErrors.desiredJobTitles}</p>
      )}
    </div>

    <div>
      <Label htmlFor="preferredIndustries">Preferred industries</Label>
      <Input id="preferredIndustries" name="preferredIndustries" placeholder="e.g., Manufacturing, Logistics, Engineering" value={formData.preferredIndustries} onChange={onInputChange} className="mt-1" data-testid="input-preferred-industries" />
    </div>

    <div>
      <Label htmlFor="preferredLocations">Preferred geographic locations</Label>
      <Input id="preferredLocations" name="preferredLocations" placeholder="e.g., Cincinnati, Indianapolis, Midwest" value={formData.preferredLocations} onChange={onInputChange} className="mt-1" data-testid="input-preferred-locations" />
    </div>

    <div>
      <Label htmlFor="openToRelocation">Open to relocation? *</Label>
      <Select value={formData.openToRelocation || undefined} onValueChange={(value) => onSelectChange('openToRelocation', value)}>
        <SelectTrigger className={`mt-1 ${validationErrors.openToRelocation ? 'border-red-500' : ''}`} data-testid="select-open-to-relocation">
          <SelectValue placeholder="Select option" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
          <SelectItem value="maybe">Maybe / Depends on opportunity</SelectItem>
        </SelectContent>
      </Select>
      {validationErrors.openToRelocation && (
        <p className="text-red-500 text-sm mt-1" data-testid="error-open-to-relocation">{validationErrors.openToRelocation}</p>
      )}
    </div>

    <div>
      <Label htmlFor="willingToTravel">Willing to travel? If yes, approximate %</Label>
      <Input id="willingToTravel" name="willingToTravel" placeholder="e.g., Yes, up to 25% travel" value={formData.willingToTravel} onChange={onInputChange} className="mt-1" data-testid="input-willing-to-travel" />
    </div>
  </div>
);

const EMPTY_VALIDATION = {};

export const CompensationScreeningSection = ({ formData, validationErrors, onInputChange, onSelectChange, onFileChange }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold text-[#0a1628] mb-4">Compensation & Availability</h3>

    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="desiredSalaryRange">Desired salary range</Label>
        <Input id="desiredSalaryRange" name="desiredSalaryRange" placeholder="e.g., $65,000 - $80,000" value={formData.desiredSalaryRange} onChange={onInputChange} className="mt-1" data-testid="input-desired-salary" />
      </div>
      <div>
        <Label htmlFor="bonusExpectations">Bonus / commission expectations</Label>
        <Input id="bonusExpectations" name="bonusExpectations" placeholder="e.g., 10% annual bonus" value={formData.bonusExpectations} onChange={onInputChange} className="mt-1" data-testid="input-bonus-expectations" />
      </div>
    </div>

    <div>
      <Label htmlFor="availabilityToStart">Availability to start / notice period</Label>
      <Input id="availabilityToStart" name="availabilityToStart" placeholder="e.g., Immediately, 2 weeks notice" value={formData.availabilityToStart} onChange={onInputChange} className="mt-1" data-testid="input-availability" />
    </div>

    <div>
      <Label htmlFor="idealNextRole">Describe your ideal next role</Label>
      <Textarea id="idealNextRole" name="idealNextRole" rows={4} value={formData.idealNextRole} onChange={onInputChange} className="mt-1" data-testid="textarea-ideal-role" />
    </div>

    <div className="border-t pt-6 mt-6">
      <h4 className="text-xl font-bold text-[#0a1628] mb-4">Screening Questions</h4>

      <div className="space-y-4">
        <ScreeningSelect label="Are you legally authorized to work in the United States? *" field="authorizedToWork" formData={formData} validationErrors={validationErrors} onSelectChange={onSelectChange} testId="select-authorized-to-work" />
        <ScreeningSelect label="Would you require visa sponsorship now or in the future?" field="requiresSponsorship" formData={formData} validationErrors={EMPTY_VALIDATION} onSelectChange={onSelectChange} testId="select-requires-sponsorship" />
        <ScreeningSelect label="Are you willing to complete a background check?" field="willingBackgroundCheck" formData={formData} validationErrors={EMPTY_VALIDATION} onSelectChange={onSelectChange} testId="select-background-check" />
        <ScreeningSelect label="Are you willing to complete a drug screening if required?" field="willingDrugScreen" formData={formData} validationErrors={EMPTY_VALIDATION} onSelectChange={onSelectChange} testId="select-drug-screen" />
        <ScreeningSelect label="Are you currently interviewing with other companies?" field="currentlyInterviewing" formData={formData} validationErrors={EMPTY_VALIDATION} onSelectChange={onSelectChange} testId="select-currently-interviewing" />
      </div>
    </div>

    <div className="border-t pt-6 mt-6">
      <Label htmlFor="resume">Upload Resume (PDF, DOC, DOCX) - Optional</Label>
      <div className="mt-2 flex items-center gap-4">
        <Input id="resume" name="resume" type="file" accept=".pdf,.doc,.docx" onChange={onFileChange} className="flex-1" data-testid="input-resume-upload" />
      </div>
      {formData.resumeFile && (
        <p className="text-sm text-gray-600 mt-2" data-testid="resume-file-name">Selected: {formData.resumeFile.name}</p>
      )}
    </div>
  </div>
);

const ScreeningSelect = ({ label, field, formData, validationErrors, onSelectChange, testId }) => (
  <div>
    <Label htmlFor={field}>{label}</Label>
    <Select value={formData[field] || undefined} onValueChange={(value) => onSelectChange(field, value)}>
      <SelectTrigger className={`mt-1 ${validationErrors[field] ? 'border-red-500' : ''}`} data-testid={testId}>
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="yes">Yes</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
    {validationErrors[field] && (
      <p className="text-red-500 text-sm mt-1">{validationErrors[field]}</p>
    )}
  </div>
);
